/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LinkedInStyleSelect from '../components/LinkedInStyleSelect';
import { buildApiUrl } from '../config/api';
import { 
  countries,
  currencies,
  industries,
  departments,
  allSkills,
  jobTypes,
  workModes,
  experienceLevels,
  educationLevels,
  benefits,
  salaryTypes,
  salaryPeriods,
  applyMethods,
  visibilityOptions
} from '../data/formOptions';

const PostJob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  
  // Determine user role and preferred back route
  const userRoleRaw = (localStorage.getItem('role') || localStorage.getItem('userType') || '');
  const roleLower = userRoleRaw.toLowerCase();
  const fromRoute = (location.state && location.state.from) || null;
  const defaultDashboardRoute = roleLower.includes('association')
    ? '/association-dashboard?section=jobs'
    : roleLower.includes('employer')
      ? '/employer-dashboard'
      : '/recruiter-dashboard';
  const dashboardRoute = fromRoute || defaultDashboardRoute;

  // Fetch user profile on mount to get company information
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setProfileLoading(false);
          return;
        }
        
        const response = await fetch(buildApiUrl('/api/user/profile'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const profileData = await response.json();
          setUserProfile(profileData);
          console.log('📋 PostJob - Fetched user profile:', {
            companyName: profileData.companyName,
            companyEmail: profileData.companyEmail,
            firstName: profileData.firstName
          });
        } else {
          console.warn('⚠️ PostJob - Failed to fetch profile:', response.status);
        }
      } catch (error) {
        console.error('❌ PostJob - Error fetching profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    
    fetchUserProfile();
  }, []);
  
  const isRecruiter = roleLower === 'recruiter';
  
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    companyName: '', // Used when recruiter posts for a client company
    jobType: 'Full-time',
    workMode: 'On-site',
    experienceLevel: '',
    vacancies: 1,
    industry: [],
    department: [],
    skills: [],
    tools: [],
    education: 'Any',
    
    // Job Description
    summary: '',
    responsibilities: '',
    desiredProfile: '',
    companyOverview: '',
    
    // Salary & Benefits
    salaryType: 'Negotiable',
    salaryPeriod: 'yearly',
    salaryMin: '',
    salaryMax: '',
    currency: 'USD',
    benefits: [],
    perks: [],
    
    // Location
    country: 'Kenya',
    state: '',
    city: '',
    officeAddress: '',
    postalCode: '',
    
    // Application Details
    applicationDeadline: '',
    preferredJoiningDate: '',
    applyMethod: 'Apply through AksharJobs portal',
    contactEmail: '',
    hrName: '',
    visibility: 'public',
    // Privacy controls
    showCompanyName: true,
    showHrName: true
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const industryArray = Array.isArray(formData.industry) ? formData.industry : (formData.industry ? [formData.industry] : []);
    if (!formData.title || industryArray.length === 0 || !formData.summary || !formData.contactEmail) {
      alert('Please fill in all required fields: Job Title, Industry, Summary, and Contact Email');
      return;
    }
    if (isRecruiter && (!formData.companyName || formData.companyName.trim() === '')) {
      alert('Please enter the company name you are posting this job for.');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Get company name: recruiters enter it in the form; employers use profile
      let companyName = '';
      let companyWebsite = '';
      
      if (isRecruiter) {
        companyName = (formData.companyName || '').trim();
        companyWebsite = userProfile?.companyWebsite || '';
      } else {
        // For employer/association/NGO: use companyName, associationName, universityName, or ngoName
        const getOrgName = (p) => (p?.companyName || p?.associationName || p?.universityName || p?.ngoName || '').trim();
        if (userProfile) {
          companyName = getOrgName(userProfile);
          companyWebsite = userProfile.companyWebsite || userProfile.officialEmail || '';
        } else {
          try {
            const response = await fetch(buildApiUrl('/api/user/profile'), {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const profileData = await response.json();
              companyName = getOrgName(profileData);
              companyWebsite = profileData.companyWebsite || profileData.officialEmail || '';
              setUserProfile(profileData);
            }
          } catch (error) {
            console.error('Error fetching profile:', error);
          }
        }
        if (!companyName) {
          const userDataString = localStorage.getItem('user');
          const userData = userDataString ? JSON.parse(userDataString) : null;
          const employerProfileString = localStorage.getItem('employerProfile');
          const employerProfile = employerProfileString ? JSON.parse(employerProfileString) : null;
          companyName = getOrgName(employerProfile) || getOrgName(userData) ||
            (employerProfile?.companyName || employerProfile?.company_name || userData?.companyName || userData?.company_name || '');
          companyWebsite = companyWebsite || employerProfile?.companyWebsite || employerProfile?.company_website || userData?.companyWebsite || userData?.company_website || '';
        }
        if (!companyName || companyName.trim() === '') {
          alert('Please complete your profile before posting a job so applicants see the correct organization name.\n\nGo to "My Profile" to complete your organization information (e.g. Association Name, Company Name).');
          setLoading(false);
          return;
        }
      }
      
      // Transform data to match backend expectations
      const jobData = {
        title: formData.title,
        companyName: companyName,
        companyWebsite: companyWebsite,
        job_type: formData.jobType,
        work_mode: formData.workMode,
        experience_level: formData.experienceLevel,
        vacancies: formData.vacancies,
        industry: formData.industry,
        department: formData.department,
        skills: formData.skills,
        tools: formData.tools,
        education: formData.education,
        
        // Combine all description fields
        description: formData.summary,
        responsibilities: formData.responsibilities,
        requirements: formData.desiredProfile,
        company_overview: formData.companyOverview,
        
        // Salary
        salary_min: formData.salaryMin,
        salary_max: formData.salaryMax,
        salary_currency: formData.currency,
      salary_period: formData.salaryPeriod,
        benefits: formData.benefits,
        perks: formData.perks,
        
        // Location
        country: formData.country,
        state: formData.state,
        city: formData.city,
        location: `${formData.city}, ${formData.state}, ${formData.country}`,
        office_address: formData.officeAddress,
        postal_code: formData.postalCode,
        
        // Application Details
        application_deadline: formData.applicationDeadline || null,
        preferred_joining_date: formData.preferredJoiningDate,
        apply_method: formData.applyMethod,
        contact_email: formData.contactEmail,
        hr_name: formData.hrName,
        visibility: formData.visibility,
        // Privacy flags (send in both formats for backward compatibility)
        show_company_name: formData.showCompanyName,
        showCompanyName: formData.showCompanyName,
        show_hr_name: formData.showHrName,
        showHrName: formData.showHrName,
        
        postedAt: new Date().toISOString(),
        status: 'active'
      };
      
      console.log('📤 Sending job data:', jobData);
      
      const response = await fetch(buildApiUrl('/api/jobs/add_job'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Job posted successfully:', result);
        alert(`✅ Job posted successfully!\n\nJob ID: ${result.job_id}\n\nYour job is now live and visible to candidates!`);
        navigate(dashboardRoute);
      } else {
        const error = await response.json();
        console.error('❌ Error response:', error);
        alert(`❌ Error: ${error.message || error.error || 'Failed to post job'}`);
      }
    } catch (error) {
      console.error('❌ Error posting job:', error);
      alert(`❌ An error occurred while posting the job\n\nError: ${error.message}\n\nPlease check your internet connection and try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Post a Job</h1>
              <p className="mt-2 text-sm text-slate-600">
                Create a detailed job posting to attract the best candidates
              </p>
          </div>
            <div className="flex w-full gap-3">
              <button
                type="button"
                onClick={() => navigate(dashboardRoute)}
                className="flex-1 min-w-0 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style={{
                  background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box'
                }}
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={() => navigate(dashboardRoute)}
                className="flex-1 min-w-0 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style={{
                  background: 'linear-gradient(135deg, #20B2AA 0%, #17a2b8 100%)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section 1: Basic Job Information */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Job Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Job Title */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title <span className="text-red-500">*</span>
                </label>
                      <input
                        type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Senior Full Stack Developer"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                        required
                      />
                    </div>
              
              {/* Company Name - recruiter only (recruiters post for different companies) */}
              {isRecruiter && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    placeholder="e.g., Which company are you posting this job for?"
                    className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter the company you are posting this job for</p>
                </div>
              )}
                    
              {/* Job Type */}
              <LinkedInStyleSelect
                label="Job Type"
                value={formData.jobType}
                onChange={(val) => handleChange('jobType', val)}
                options={jobTypes}
                containerClassName="w-full"
                openUpwards={true}
                required
              />

              {/* Work Mode */}
              <LinkedInStyleSelect
                label="Work Mode"
                value={formData.workMode}
                onChange={(val) => handleChange('workMode', val)}
                options={workModes}
                containerClassName="w-full"
                openUpwards={true}
                required
              />

              {/* Experience Level */}
              <LinkedInStyleSelect
                label="Experience Level"
                value={formData.experienceLevel}
                onChange={(val) => handleChange('experienceLevel', val)}
                options={experienceLevels}
                placeholder="Select experience level"
                containerClassName="w-full"
                openUpwards={true}
                        required
                      />

              {/* Vacancies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Vacancies
                </label>
                      <input
                  type="number"
                  min="1"
                  value={formData.vacancies}
                  onChange={(e) => handleChange('vacancies', parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                      />
                    </div>
                    
              {/* Industry */}
              <LinkedInStyleSelect
                label="Industry / Sector"
                        value={formData.industry}
                onChange={(val) => handleChange('industry', val)}
                options={industries}
                placeholder="Select industry"
                searchable
                multiple
                containerClassName="w-full"
                openUpwards={true}
                        required
              />

              {/* Department */}
              <LinkedInStyleSelect
                label="Department / Functional Area"
                value={formData.department}
                onChange={(val) => handleChange('department', val)}
                options={departments}
                placeholder="Select department"
                searchable
                multiple
                containerClassName="w-full"
                openUpwards={true}
              />

              {/* Skills Required */}
              <div className="md:col-span-2">
                <LinkedInStyleSelect
                  label="Skills Required"
                  value={formData.skills}
                  onChange={(val) => handleChange('skills', val)}
                  options={allSkills}
                  placeholder="Select skills or type custom skills"
                  searchable
                  multiple
                  allowCustom={true}
                  containerClassName="w-full"
                  openUpwards={true}
                />
              </div>
                    
              {/* Tools / Technologies */}
              <div className="md:col-span-2">
                <LinkedInStyleSelect
                  label="Tools / Technologies"
                  value={formData.tools}
                  onChange={(val) => handleChange('tools', val)}
                  options={allSkills}
                  placeholder="Select tools or type custom tools"
                  searchable
                  multiple
                  allowCustom={true}
                  containerClassName="w-full"
                  openUpwards={true}
                />
              </div>
                  
              {/* Education Requirement */}
              <LinkedInStyleSelect
                label="Education Requirement"
                value={formData.education}
                onChange={(val) => handleChange('education', val)}
                options={educationLevels}
                containerClassName="w-full"
              />
                  </div>
                </div>

          {/* Section 2: Job Description & Responsibilities */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Description & Responsibilities</h2>
            
            <div className="space-y-4 sm:space-y-6">
              {/* Job Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => handleChange('summary', e.target.value)}
                  placeholder="Brief overview of the role (2-4 sentences)"
                  rows="4"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                        required
                      />
                    </div>
                    
              {/* Key Responsibilities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Responsibilities
                </label>
                <textarea
                  value={formData.responsibilities}
                  onChange={(e) => handleChange('responsibilities', e.target.value)}
                  placeholder="• Main duty 1&#10;• Main duty 2&#10;• Main duty 3&#10;(One responsibility per line)"
                  rows="6"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                  </div>
                  
              {/* Desired Candidate Profile */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desired Candidate Profile
                </label>
                    <textarea
                  value={formData.desiredProfile}
                  onChange={(e) => handleChange('desiredProfile', e.target.value)}
                  placeholder="Ideal experience, skills, and personal qualities we're looking for"
                  rows="4"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                    />
                  </div>
                  
              {/* Company Overview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Overview
                </label>
                <textarea
                  value={formData.companyOverview}
                  onChange={(e) => handleChange('companyOverview', e.target.value)}
                  placeholder="About your company, culture, and mission"
                  rows="4"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                    </div>
                  </div>
                </div>

          {/* Section 3: Salary & Benefits */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Salary & Benefits</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Salary Type */}
              <LinkedInStyleSelect
                label="Salary Type"
                value={formData.salaryType}
                onChange={(val) => handleChange('salaryType', val)}
                options={salaryTypes}
                containerClassName="w-full"
              />

              {/* Currency */}
              <LinkedInStyleSelect
                label="Currency"
                value={formData.currency}
                onChange={(val) => handleChange('currency', val)}
                options={currencies}
                searchable
                containerClassName="w-full"
              />

              {/* Salary Period */}
              <LinkedInStyleSelect
                label="Salary Period"
                value={formData.salaryPeriod}
                onChange={(val) => handleChange('salaryPeriod', val)}
                options={salaryPeriods}
                placeholder="Per Year"
                containerClassName="w-full"
              />

              {/* Salary Min */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Salary
                </label>
                      <input
                  type="number"
                  value={formData.salaryMin}
                  onChange={(e) => handleChange('salaryMin', e.target.value)}
                  placeholder="e.g., 50000"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                      />
                    </div>
                    
              {/* Salary Max */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Salary
                </label>
                <input
                  type="number"
                  value={formData.salaryMax}
                  onChange={(e) => handleChange('salaryMax', e.target.value)}
                  placeholder="e.g., 80000"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                    </div>
                    
              {/* Benefits */}
              <div className="md:col-span-2">
                <LinkedInStyleSelect
                  label="Benefits Offered"
                  value={formData.benefits}
                  onChange={(val) => handleChange('benefits', val)}
                  options={benefits}
                  placeholder="Select benefits"
                  searchable
                  multiple
                  containerClassName="w-full"
                      />
                    </div>
                  </div>
                </div>

          {/* Section 4: Job Location */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Location</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Country */}
              <LinkedInStyleSelect
                label="Country"
                value={formData.country}
                onChange={(val) => handleChange('country', val)}
                options={countries}
                searchable
                        required
              />

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State / Province
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g., Nairobi County"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                    </div>
                    
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                      <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., Nairobi"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                      />
                    </div>
                    
              {/* Postal Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code
                </label>
                      <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleChange('postalCode', e.target.value)}
                  placeholder="e.g., 00100"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                  </div>
                  
              {/* Office Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Office Address (Optional)
                </label>
                      <input
                        type="text"
                  value={formData.officeAddress}
                  onChange={(e) => handleChange('officeAddress', e.target.value)}
                  placeholder="Street address (for on-site or hybrid roles)"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                    </div>
                  </div>
                </div>

          {/* Section 5: Application Details */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Application Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application Deadline
                </label>
                      <input
                        type="date"
                        value={formData.applicationDeadline}
                  onChange={(e) => handleChange('applicationDeadline', e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                      />
                    </div>
                    
              {/* Preferred Joining Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Joining Date
                </label>
                <input
                  type="date"
                  value={formData.preferredJoiningDate}
                  onChange={(e) => handleChange('preferredJoiningDate', e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
                    </div>
                    
              {/* Application Method */}
              <LinkedInStyleSelect
                label="Application Method"
                value={formData.applyMethod}
                onChange={(val) => handleChange('applyMethod', val)}
                options={['Apply through AksharJobs portal']}
              />

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleChange('contactEmail', e.target.value)}
                  placeholder="hr@company.com"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                        required
                />
                  </div>
                  
              {/* HR Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HR / Recruiter Name
                </label>
                <input
                  type="text"
                  value={formData.hrName}
                  onChange={(e) => handleChange('hrName', e.target.value)}
                  placeholder="e.g., John Doe"
                  className="w-full px-4 py-2.5 border-2 border-gray-800 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-md"
                />
              </div>

              {/* Visibility */}
              <div className="flex md:justify-end">
                <div className="w-full md:w-3/4 lg:w-2/3">
                  <LinkedInStyleSelect
                    label="Job Visibility"
                    value={formData.visibility}
                    onChange={(val) => handleChange('visibility', val)}
                    options={visibilityOptions}
                    containerClassName="w-full"
                  />
                </div>
              </div>
              
              {/* Privacy: Show company name */}
              <div className="flex items-start gap-2 md:col-span-2">
                <input
                  id="show-company-name"
                  type="checkbox"
                  checked={formData.showCompanyName}
                  onChange={(e) => handleChange('showCompanyName', e.target.checked)}
                  className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <label htmlFor="show-company-name" className="block text-sm font-medium text-gray-700">
                    Show company name on public job listing
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Keep this checked to display your company name to candidates. Uncheck if you prefer a confidential posting.
                  </p>
                </div>
              </div>

              {/* Privacy: Show hiring manager name */}
              <div className="flex items-start gap-2 md:col-span-2">
                <input
                  id="show-hr-name"
                  type="checkbox"
                  checked={formData.showHrName}
                  onChange={(e) => handleChange('showHrName', e.target.checked)}
                  className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <label htmlFor="show-hr-name" className="block text-sm font-medium text-gray-700">
                    Show HR / recruiter name to candidates
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Keep this checked if you want applicants to see the hiring manager&apos;s name. Uncheck to keep this private.
                  </p>
                </div>
              </div>
                      </div>
                    </div>
                    
          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600">
                * Required fields must be filled
              </p>
              <div className="flex w-full gap-3">
                    <button
                      type="button"
                  onClick={() => navigate(dashboardRoute)}
                  className="flex-1 min-w-0 h-12 px-6 py-0 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  style={{
                    background: 'linear-gradient(135deg, #20B2AA 0%, #17a2b8 100%)',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box'
                  }}
                >
                  Cancel
                      </button>
                      <button
                        type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 min-w-0 h-12 px-8 py-0 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box'
                  }}
                >
                  {loading ? 'Publishing...' : 'Publish Job'}
                      </button>
                  </div>
                </div>
              </div>
            </form>
      </div>
    </div>
  );
};

export default PostJob;
